import {useCallback, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  ResponsiveSheet,
} from '@chekinapp/ui';
import {useIVCompleted} from 'hooks/iv/useIVCompleted';
import {useGetGuestId} from 'hooks/useGetGuestId';
import phoneCompleted from 'assets/img/phone-completed.svg?no-inline';
import redirectToDesktop from 'assets/img/redirect-to-desktop.svg?no-inline';
import {VerificationCompletedContext} from './context';

export const VerificationCompletedModal = ({open}: {open?: boolean}) => {
  const {proceed, closeModal} = useContext(VerificationCompletedContext);
  const {t} = useTranslation();
  const [isReturnToDesktop, setReturnToDesktop] = useState(false);
  const {returnToDesktop, continueOnPhone} = useIVCompleted();
  const guestId = useGetGuestId();
  const isOnlyVerification = Boolean(guestId);

  const returnToDesktopHandler = useCallback(() => {
    setReturnToDesktop(true);
    returnToDesktop();
  }, [returnToDesktop]);

  const continueOnPhoneHandler = useCallback(() => {
    proceed();
    continueOnPhone();
  }, [continueOnPhone, proceed]);

  useEffect(() => {
    if (open && isOnlyVerification) {
      returnToDesktop();
    }
  }, [isOnlyVerification, open, returnToDesktop]);

  const canContinueOnPhone = !isOnlyVerification && !isReturnToDesktop;

  const {image, title, lines} = isOnlyVerification
    ? {
        image: redirectToDesktop,
        title: t('identity_verification_is_completed'),
        lines: [
          t('return_to_your_computer_to_review_your_details'),
          t('you_can_close_this_page'),
        ],
      }
    : canContinueOnPhone
    ? {
        image: phoneCompleted,
        title: t('identity_verification_is_completed'),
        lines: [t('continue_with_check_in_form')],
      }
    : {
        image: redirectToDesktop,
        title: t('continue_your_registration_process_on_your_computer'),
        lines: [t('you_can_close_this_page')],
      };

  return (
    <ResponsiveSheet
      open={Boolean(open)}
      onClose={closeModal}
      disableDrag
      title={title}
      hideTitle
      dialogClassName="sm:max-w-[420px]"
    >
      <Empty density="compact" className="gap-6 border-0 p-0">
        <EmptyHeader>
          <img src={image} alt="" className="mb-3 h-20 select-none" />
          <EmptyTitle aria-hidden="true">{title}</EmptyTitle>
          <EmptyDescription>
            <div className="flex flex-col gap-1">
              {lines.map(line => (
                <span key={line}>{line}</span>
              ))}
            </div>
          </EmptyDescription>
        </EmptyHeader>

        {canContinueOnPhone && (
          <EmptyContent className="gap-2">
            <Button size="lg" onClick={continueOnPhoneHandler} className="w-full">
              {t('continue_on_phone')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={returnToDesktopHandler}
              className="w-full"
            >
              {t('return_to_desktop')}
            </Button>
          </EmptyContent>
        )}
      </Empty>
    </ResponsiveSheet>
  );
};
